import { createRoute } from '@tanstack/react-router'
import { Clock, Hourglass, Lock, Mic, Video } from 'lucide-react'
import { type FormEvent, type ReactNode, useEffect, useState } from 'react'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { api, type GuestPreview } from '../lib/api'
import { Route as rootRoute } from './__root'

// #751: public guest page for one-time meeting links (`/g#<token>`). Renders
// in the login layout (see isAuthRoute in __root). The token lives in the URL
// fragment — never sent to any server by the browser — is removed from the
// address bar immediately and only ever travels in a POST body. The page
// calls no logged-in endpoint; the global api.me() just resolves to null.

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/g',
  component: GuestPage,
})

const NAME_KEY = 'ember_guest_name'
/** Only admins issue links (#751, Variante a) — that is Dennis. */
const ISSUER_NAME = 'Dennis'

type GuestWindow = {
  location: { hash: string; pathname: string; search: string }
  history: { replaceState: (data: unknown, unused: string, url: string) => void }
}

/** Reads the token from the fragment and strips it from the address bar and
 * history right away. Returns '' when there is none. */
export function readGuestToken(win: GuestWindow): string {
  const token = win.location.hash.replace(/^#/, '')
  if (!token) return ''
  win.history.replaceState(null, '', `${win.location.pathname}${win.location.search}`)
  return token
}

/** Mirrors the server rule: trimmed 1–60 characters, no control/format chars. */
export function isValidGuestName(name: string): boolean {
  const trimmed = name.trim()
  const length = [...trimmed].length
  return length >= 1 && length <= 60 && !/\p{C}/u.test(trimmed)
}

function session(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

// Token only in memory (never in storage, spec 2.2): survives the second
// StrictMode initializer call, but not a reload — then the guest opens the
// link again.
let takenToken = ''

/** Token for this page: fresh from the fragment, else the one read earlier. */
export function takeToken(win: GuestWindow = window): string {
  const fromHash = readGuestToken(win)
  if (fromHash) takenToken = fromHash
  return takenToken
}

const dayFmt = new Intl.DateTimeFormat('de-DE', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
})
const timeFmt = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' })

function meetingLabel(startIso: string, endIso: string): string {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const sameDay = start.toDateString() === end.toDateString()
  return sameDay
    ? `${dayFmt.format(start)} · ${timeFmt.format(start)}–${timeFmt.format(end)}`
    : `${dayFmt.format(start)} ${timeFmt.format(start)} → ${dayFmt.format(end)} ${timeFmt.format(end)}`
}

/** An unused link works until min(expiresAt, meeting end + 2 h). */
function remainingLabel(expiresIso: string, endIso: string, now: Date): string {
  const deadline = Math.min(
    new Date(expiresIso).getTime(),
    new Date(endIso).getTime() + 2 * 60 * 60 * 1000,
  )
  const ms = deadline - now.getTime()
  if (ms >= 60 * 60 * 1000) return `${Math.floor(ms / (60 * 60 * 1000))} h`
  return `${Math.max(1, Math.floor(ms / 60_000))} min`
}

type View =
  | { kind: 'loading' }
  | { kind: 'ok'; info: Extract<GuestPreview, { state: 'ok' }> }
  | { kind: 'unavailable' }
  | { kind: 'used' }
  | { kind: 'error'; slowDown: boolean }

function GuestPage() {
  const [token, setToken] = useState(() => takeToken())
  const [view, setView] = useState<View>({ kind: 'loading' })
  const [name, setName] = useState(() => session()?.getItem(NAME_KEY) ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Kein Referer an Jitsi o. ä. — zusätzlich zum Header aus nginx.conf.
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'referrer'
    meta.content = 'no-referrer'
    document.head.appendChild(meta)
    return () => meta.remove()
  }, [])

  // Ein neu eingefügter Link im selben Tab ist nur ein Hash-Wechsel.
  useEffect(() => {
    const onHash = () => {
      const t = takeToken()
      if (t) setToken(t)
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // Zurück aus Jitsi über den bfcache: Knopf wieder freigeben, Stand neu laden.
  const [reloadTick, setReloadTick] = useState(0)
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return
      setBusy(false)
      setReloadTick((n) => n + 1)
    }
    window.addEventListener('pageshow', onShow)
    return () => window.removeEventListener('pageshow', onShow)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadTick re-runs the preview
  useEffect(() => {
    // Antwort eines älteren Tokens (Hash-Wechsel während der Anfrage) verwerfen.
    let current = true
    if (!token) {
      setView({ kind: 'unavailable' })
      return
    }
    setView({ kind: 'loading' })
    void api.guestPreview(token).then((res) => {
      if (!current) return
      if (res.state === 'ok') setView({ kind: 'ok', info: res })
      else if (res.state === 'used') setView({ kind: 'used' })
      else if (res.state === 'unavailable') setView({ kind: 'unavailable' })
      else setView({ kind: 'error', slowDown: res.state === 'slow_down' })
    })
    return () => {
      current = false
    }
  }, [token, reloadTick])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    if (!isValidGuestName(name)) {
      setError('Bitte gib Deinen Namen ein (1–60 Zeichen, ohne Sonderzeichen).')
      return
    }
    setBusy(true)
    setError(null)
    const res = await api.guestJoin(token, name.trim())
    if (res.state === 'ok') {
      session()?.setItem(NAME_KEY, name.trim())
      window.location.assign(res.joinUrl)
      return
    }
    setBusy(false)
    if (res.state === 'used') setView({ kind: 'used' })
    else if (res.state === 'unavailable') setView({ kind: 'unavailable' })
    else if (res.state === 'invalid_name')
      setError('Bitte gib Deinen Namen ein (1–60 Zeichen, ohne Sonderzeichen).')
    else if (res.state === 'slow_down')
      setError(
        'Gerade sind zu viele Anfragen unterwegs. Bitte versuch es in ein paar Minuten nochmal.',
      )
    else setError('Beitreten klappt gerade nicht. Bitte versuch es gleich nochmal.')
  }

  return (
    <>
      {view.kind === 'loading' && (
        <Card className="w-full max-w-md card-section">
          <CardContent className="p-6 text-center text-sm text-text-tertiary">Lade …</CardContent>
        </Card>
      )}
      {view.kind === 'ok' && (
        <GuestJoinView
          hostFirstName={view.info.hostFirstName}
          startTime={view.info.startTime}
          endTime={view.info.endTime}
          expiresAt={view.info.expiresAt}
          rejoin={view.info.rejoin}
          name={name}
          onNameChange={setName}
          onSubmit={onSubmit}
          busy={busy}
          error={error}
          now={new Date()}
        />
      )}
      {view.kind === 'unavailable' && <GuestExpiredView />}
      {view.kind === 'used' && <GuestUsedView />}
      {view.kind === 'error' && (
        <GuestMessage
          icon={<Clock className="h-7 w-7" />}
          tone="warning"
          title="Kurz warten"
          text={
            view.slowDown
              ? 'Gerade sind zu viele Anfragen unterwegs. Bitte lade die Seite in ein paar Minuten neu.'
              : 'Die Seite konnte nicht geladen werden. Bitte lade sie gleich neu.'
          }
        />
      )}
      <p className="mt-8 text-xs text-text-tertiary">Gastzugang · kein Konto nötig</p>
    </>
  )
}

export function GuestJoinView(props: {
  hostFirstName: string
  startTime: string
  endTime: string
  expiresAt: string
  rejoin: boolean
  name: string
  onNameChange: (name: string) => void
  onSubmit: (e: FormEvent) => void
  busy: boolean
  error: string | null
  now: Date
}) {
  const host = props.hostFirstName.trim()
  return (
    <Card className="w-full max-w-md card-section">
      <CardHeader className="pt-7">
        <CardTitle className="font-serif text-3xl">Du bist eingeladen</CardTitle>
        <CardDescription>
          {host
            ? `${host} lädt Dich zu einem Video-Meeting ein.`
            : 'Du wirst zu einem Video-Meeting eingeladen.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={props.onSubmit} className="space-y-4">
          <div className="flex items-center gap-3 rounded-xl border border-border-subtle bg-bg-overlay/40 p-3">
            <div className="h-10 w-10 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
              <Video className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">Video-Meeting</p>
              <p className="text-xs text-text-tertiary">
                {meetingLabel(props.startTime, props.endTime)}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="guest-name">Dein Name</Label>
            <Input
              id="guest-name"
              autoComplete="name"
              maxLength={60}
              placeholder="Vor- und Nachname"
              value={props.name}
              onChange={(e) => props.onNameChange(e.target.value)}
              required
              className="touch:h-11 h-11 text-base"
              aria-describedby="guest-name-hint"
            />
            <p id="guest-name-hint" className="text-xs text-text-tertiary">
              So sehen Dich die anderen im Raum.
            </p>
            {props.error && (
              <p className="text-xs text-danger" role="alert">
                {props.error}
              </p>
            )}
          </div>
          <Button type="submit" className="w-full" size="lg" disabled={props.busy}>
            <Video className="h-4 w-4" />
            {props.busy ? 'Trete bei …' : 'Beitreten'}
          </Button>
          <p className="flex items-start gap-1.5 text-xs text-text-tertiary">
            <Mic className="h-3.5 w-3.5 mt-px shrink-0" />
            Gleich fragt Dein Browser nach Kamera und Mikrofon.
          </p>
          <div className="flex items-center justify-center gap-1.5 border-t border-border-subtle pt-3 text-2xs text-text-tertiary">
            <Clock className="h-3.5 w-3.5" />
            {props.rejoin
              ? 'Wiederkommen in diesem Browser bis Meeting-Ende'
              : `Link gilt noch ${remainingLabel(props.expiresAt, props.endTime, props.now)} · nur einmal nutzbar`}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

function GuestMessage({
  icon,
  tone,
  title,
  text,
  hint,
}: {
  icon: ReactNode
  tone: 'warning' | 'danger'
  title: string
  text: string
  hint?: string
}) {
  return (
    <Card className="w-full max-w-md card-section">
      <CardHeader className="items-center text-center gap-3 pt-8">
        <div
          className={
            tone === 'warning'
              ? 'h-14 w-14 rounded-full bg-warning/15 text-warning flex items-center justify-center'
              : 'h-14 w-14 rounded-full bg-danger/15 text-danger flex items-center justify-center'
          }
        >
          {icon}
        </div>
        <CardTitle className="font-serif text-3xl">{title}</CardTitle>
        <CardDescription>{text}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {hint && (
          <div className="rounded-xl border border-border-subtle bg-bg-overlay/40 p-3 text-sm text-text-secondary">
            {hint}
          </div>
        )}
        <p className="text-center text-2xs text-text-tertiary">
          Aus Sicherheitsgründen zeigen wir hier keine Details zum Termin.
        </p>
      </CardContent>
    </Card>
  )
}

/** Unknown, malformed, expired and revoked links all land here. */
function GuestExpiredView() {
  return (
    <GuestMessage
      icon={<Hourglass className="h-7 w-7" />}
      tone="warning"
      title="Link abgelaufen"
      text="Dieser Gastlink galt 24 Stunden und ist nicht mehr gültig."
      hint={`Bitte frag bei ${ISSUER_NAME} nach einem neuen Link. Das geht in einer Minute.`}
    />
  )
}

function GuestUsedView() {
  return (
    <GuestMessage
      icon={<Lock className="h-7 w-7" />}
      tone="danger"
      title="Link schon benutzt"
      text="Mit diesem Gastlink ist schon jemand beigetreten. Jeder Link gilt nur einmal."
      hint={`Warst Du das und bist rausgeflogen? Im selben Browser kommst Du bis Meeting-Ende wieder rein. Sonst bitte ${ISSUER_NAME} um einen neuen Link.`}
    />
  )
}
